import React, { useMemo } from 'react';

import { Alert, CircularProgress, Paper, Stack, Typography } from '@mui/material';

import { useGetPromoActivitySummary } from '~/hooks/apis/promo/useGetPromoActivitySummary';

import { type PromoActivitySummaryTotals } from '~/services/PromoService';

type EntityType = 'promo' | 'voucher';

const EMPTY_TOTALS: PromoActivitySummaryTotals = {
  eventCount: 0,
  usageCount: 0,
  assignmentCount: 0,
  byEventType: {},
  recentApplyStats: {
    requestCount: 0,
    successfulCount: 0,
    unsuccessfulRequestCount: 0,
    unsuccessfulLogCount: 0,
  },
};

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unable to load recent activity.';
}

function renderActivityMetric(label: string, value: number) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 88 }}>
      <Typography color="text.secondary" variant="caption">
        {label}
      </Typography>
      <Typography variant="body1">{value.toLocaleString()}</Typography>
    </Stack>
  );
}

/** Compact recent-activity totals for one promo or voucher; the aggregate tester remains available for cross-entity analysis. */
export default function PromoActivityEntitySummaryCard({
  entityId,
  entityType,
}: {
  entityId: string;
  entityType: EntityType;
}) {
  const { data, error, isFetching } = useGetPromoActivitySummary({ enabled: Boolean(entityId) });
  const totals = useMemo(() => {
    if (!data) {
      return EMPTY_TOTALS;
    }
    return (entityType === 'promo' ? data.promoCodes : data.vouchers)[entityId] ?? EMPTY_TOTALS;
  }, [data, entityId, entityType]);
  const failures = totals.recentApplyStats.unsuccessfulRequestCount + totals.recentApplyStats.unsuccessfulLogCount;

  if (error) {
    return <Alert severity="warning">Recent activity is unavailable: {getErrorMessage(error)}</Alert>;
  }

  return (
    <Paper component="section" variant="outlined" aria-label="Recent activity" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1">Recent activity</Typography>
          {isFetching && <CircularProgress size={16} aria-label="Loading recent activity" />}
        </Stack>
        {entityType === 'promo' ? (
          <Stack direction="row" spacing={3} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {renderActivityMetric('Requests', totals.recentApplyStats.requestCount)}
            {renderActivityMetric('Applies', totals.recentApplyStats.successfulCount)}
            {renderActivityMetric('Uses', totals.usageCount)}
            {renderActivityMetric('Assignments', totals.assignmentCount)}
            {renderActivityMetric('Failures', failures)}
          </Stack>
        ) : (
          <Stack direction="row" spacing={3} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {renderActivityMetric('Assignments', totals.assignmentCount)}
            {renderActivityMetric('Uses', totals.usageCount)}
            {renderActivityMetric('Events', totals.eventCount)}
          </Stack>
        )}
        <Typography color="text.secondary" variant="caption">
          Cached telemetry window of about 36 hours; not a durable reporting or reconciliation source.
        </Typography>
      </Stack>
    </Paper>
  );
}
